import 'twin.macro';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/zustand/auth-store';
import { getUserTypeFromRole } from '@/utils/detect-user-type';
import { USER_TYPES } from '@/constants/constants';
import { onboardingQueries } from '@/apis/onboarding/query';
import { PATIENT_ONBOARDING_STEP } from '@/components/patientOnboarding/constants';
import LoadingElement from '@/components/shared/loadingElement';

// Keeps a patient with pending onboarding on /patients/onboarding no matter which private
// route they open directly (typed URL, bookmark, browser back/forward). Onboarding only
// unblocks once it ends through Skip or OB5 - POST /onboarding/patient/finish sets
// nextStep to null. The original destination is kept as `redirect` for when it's done.
// A failed status read lets the patient through - onboarding never locks them out.
const PatientOnboardingGuard = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const user = useAuth(state => state.user);
  const isPatient = getUserTypeFromRole(user?.role) === USER_TYPES.PATIENT;

  const statusQuery = onboardingQueries.useOnboardingStatus({ enabled: isPatient });
  const isOnboardingPending = isPatient && statusQuery.data?.nextStep === PATIENT_ONBOARDING_STEP;
  const isChecking = isPatient && statusQuery.isPending;

  useEffect(() => {
    if (isOnboardingPending) {
      const redirect = encodeURIComponent(pathname + search);
      navigate(`/patients/onboarding?redirect=${redirect}`, { replace: true });
    }
  }, [isOnboardingPending, pathname, search, navigate]);

  if (isChecking || isOnboardingPending) {
    return (
      <div tw="flex items-center justify-center min-h-screen">
        <LoadingElement width={80} height={80} />
      </div>
    );
  }

  return <Outlet />;
};

export default PatientOnboardingGuard;
